import React, { useCallback, useMemo, useState } from 'react'
import { View, Text, TextInput, Pressable, ActivityIndicator } from 'react-native'
import { BaseModal } from '../../ui/BaseModal'
import { getCreateGroupModalStyles } from './CreateGroupModal.styles'
import { useTheme } from '../../../src/theme/useTheme'
import { sanitizeInput } from '../../../src/helpers/sanitizeInput.helper'
import { useApiClient } from '../../../helpers/apiClient.helper'
import { createGroup } from '../../../src/friends/helpers/groupsApi'
import { GROUP_NAME_MIN_LENGTH, GROUP_NAME_MAX_LENGTH } from '../../../src/friends/constants/groupName'
import type { SnackbarVariant } from '../../../src/shared/stores/snackbarStore'

interface CreateGroupModalProps {
  isVisible: boolean
  onDismiss: () => void
  onGroupCreated?: () => Promise<void> | void
  /** Optional: show a snackbar message */
  onShowMessage?: (message: string, variant: SnackbarVariant) => void
  testID?: string
  /** When true, skip API call and call onGroupCreated immediately (for Storybook only) */
  skipApiForStory?: boolean
}

export const CreateGroupModal: React.FC<CreateGroupModalProps> = ({
  isVisible,
  onDismiss,
  onGroupCreated,
  onShowMessage,
  testID,
  skipApiForStory = false,
}) => {
  const theme = useTheme()
  const styles = useMemo(() => getCreateGroupModalStyles(theme), [theme])
  const apiClient = useApiClient()
  const [groupName, setGroupName] = useState('')
  const [allowMemberInvites, setAllowMemberInvites] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleChangeName = useCallback((text: string) => {
    const sanitized = sanitizeInput(text, true)
    setGroupName(sanitized.slice(0, GROUP_NAME_MAX_LENGTH))
  }, [])

  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true)

    try {
      if (skipApiForStory) {
        if (onGroupCreated) await onGroupCreated()
        if (onShowMessage) onShowMessage('Group created successfully', 'success')
        setGroupName('')
        onDismiss()
        return
      }

      const sanitizedName = sanitizeInput(groupName.trim(), true)
      await createGroup(sanitizedName, apiClient, allowMemberInvites)

      if (onGroupCreated) {
        await onGroupCreated()
      }

      if (onShowMessage) {
        onShowMessage('Group created successfully', 'success')
      }

      setGroupName('')
      onDismiss()
    } catch (submitError) {
      const message =
        submitError instanceof Error
          ? submitError.message || 'Failed to create group'
          : 'Failed to create group'
      if (onShowMessage) {
        onShowMessage(message, 'error')
      }
    } finally {
      setIsSubmitting(false)
    }
  }, [apiClient, groupName, allowMemberInvites, onDismiss, onGroupCreated, onShowMessage, skipApiForStory])

  const handleClose = useCallback(() => {
    setGroupName('')
    setIsSubmitting(false)
    onDismiss()
  }, [onDismiss])

  return (
    <BaseModal
      isVisible={isVisible}
      onDismiss={handleClose}
      blurIntensity={20}
      contentAlignment="center"
      contentContainerStyle={styles.modalContainer}
      testID={testID || 'create-group-modal'}
    >
      <View>
        <Text style={styles.title}>Create group</Text>
        <Text style={styles.subtitle}>Pick a clear name so friends know what this group is for.</Text>
        <TextInput
          value={groupName}
          onChangeText={handleChangeName}
          placeholder="Group name"
          placeholderTextColor={theme.gray[400]}
          style={styles.input}
          editable={!isSubmitting}
          maxLength={GROUP_NAME_MAX_LENGTH}
          autoCapitalize="words"
          testID={testID ? `${testID}-name-input` : 'create-group-modal-name-input'}
        />
        <Text style={styles.charCount}>{groupName.length || 0}/{GROUP_NAME_MAX_LENGTH} characters</Text>

        <Text style={styles.memberLimitHint}>
          {'Free: up to 10 members · '}
          <Text style={styles.memberLimitPro}>Pro: up to 100 members</Text>
        </Text>

        <Pressable
          style={styles.checkboxRow}
          onPress={() => setAllowMemberInvites((v) => !v)}
          disabled={isSubmitting}
          accessibilityRole="checkbox"
          accessibilityState={{ checked: allowMemberInvites }}
          accessibilityLabel="Allow members to invite others to this group."
          testID={testID ? `${testID}-allow-member-invites` : 'create-group-modal-allow-member-invites'}
        >
          <View style={[styles.checkbox, allowMemberInvites && styles.checkboxChecked]}>
            {allowMemberInvites && <Text style={styles.checkboxCheckmark}>✓</Text>}
          </View>
          <Text style={styles.checkboxLabel}>Allow members to invite others</Text>
        </Pressable>

        <View style={styles.footer}>
          <Pressable
            onPress={handleClose}
            style={styles.cancelButton}
            testID={testID ? `${testID}-cancel-button` : 'create-group-modal-cancel-button'}
          >
            <Text style={styles.cancelButtonText}>Cancel</Text>
          </Pressable>

          <Pressable
            onPress={handleSubmit}
            disabled={
              isSubmitting ||
              !groupName.trim() ||
              groupName.trim().length < GROUP_NAME_MIN_LENGTH
            }
            style={[
              styles.createButton,
              (isSubmitting ||
                !groupName.trim() ||
                groupName.trim().length < GROUP_NAME_MIN_LENGTH) &&
                styles.createButtonDisabled,
            ]}
            testID={testID ? `${testID}-create-button` : 'create-group-modal-create-button'}
          >
            {isSubmitting && (
              <ActivityIndicator
                size="small"
                color={theme.onPrimary}
                style={styles.createButtonLoader}
              />
            )}
            <Text style={styles.createButtonText}>
              {isSubmitting ? 'Creating...' : 'Create group'}
            </Text>
          </Pressable>
        </View>
      </View>
    </BaseModal>
  )
}
